import AppDocContent from '@/components/AppDocContent';
import SliderHeader from '@/components/arguments/argumentOptionsSlider/SliderHeader';
import Input from '@/components/Input';
import ToggleButton from '@/components/ToggleButton';
import { closeAppDocDrawer } from '@/redux/actions/uiActions/drawerActions.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { appDocAvailableAppSections, appDocAvailableVersionsByTypeAndObject } from '@/redux/selectors/aiSelectors.js';
import selectors from '@/redux/selectors/selectors.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from './Button.jsx';

const titleMap = {
  action: 'Action Documentation',
  app: 'App Documentation',
  auth: 'Authentication Documentation',
  api: 'API Documentation',
  filtering: 'Filtering Documentation',
  pagination: 'Pagination Documentation',
  sorting: 'Sorting Documentation'
};

const sectionLabelMap = {
  app: 'App',
  filtering: 'Filtering',
  pagination: 'Pagination',
  sorting: 'Sorting'
};

const isAppLevelResearchType = (type) => ['filtering', 'pagination', 'sorting'].includes(type);

const AppDocDrawer = () => {
  const [selectedVersion, setSelectedVersion] = useState('summary');
  const [selectedAppLevelSection, setSelectedAppLevelSection] = useState('app');
  const { open } = useSelector((state) => state.ui.appDocDrawer);
  const appId = useArgSelector(selectors.param, 'appId');
  const section = useArgSelector(selectors.param, 'section');
  const arg1 = useArgSelector(selectors.param, 'arg1');

  const availableAppSections = useSelector((state) => appDocAvailableAppSections(state));
  const actionApiDocumentationUrl = useSelector((state) => appScreenSelectors.activeAppAction(state)?.documentation?.url);
  const appApiDocumentationUrl = useSelector((state) => appScreenSelectors.activeApp(state)?.documentation?.url);
  const authDocumentationUrl = useSelector((state) => appScreenSelectors.activeAppAuth(state)?.documentation?.url);
  const isHomeRoute = !section;

  const routeType = useMemo(() => {
    if (!section) {
      return 'app';
    }
    if (section === 'actions') {
      return 'action';
    }
    if (section === 'auth') {
      return 'authentication';
    }
    return section;
  }, [section]);

  const type = useMemo(() => (isHomeRoute ? selectedAppLevelSection : routeType), [isHomeRoute, selectedAppLevelSection, routeType]);
  const objectId = useMemo(() => {
    if (!isHomeRoute) {
      if (type === 'authentication' && !arg1) {
        return 'default';
      }
      return arg1;
    }

    if (type === 'app') {
      return appId;
    }

    if (isAppLevelResearchType(type)) {
      return 'default';
    }

    return appId;
  }, [isHomeRoute, arg1, type, appId]);

  const availableVersions = useSelector((state) => appDocAvailableVersionsByTypeAndObject(state, type, objectId));

  const title = useMemo(() => titleMap[section === 'auth' ? 'auth' : type] || 'App Documentation', [section, type]);
  const docUrl = useMemo(() => {
    if (isHomeRoute) {
      return appApiDocumentationUrl;
    }

    if (type === 'authentication') {
      return authDocumentationUrl;
    }

    if (type === 'action') {
      return actionApiDocumentationUrl;
    }

    return appApiDocumentationUrl;
  }, [isHomeRoute, type, appApiDocumentationUrl, authDocumentationUrl, actionApiDocumentationUrl]);

  const sectionOptions = useMemo(() => (availableAppSections ?? []).map((item) => ({ value: item, label: sectionLabelMap[item] || item })), [availableAppSections]);
  const versionOptions = useMemo(() => (availableVersions ?? []).map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) })), [availableVersions]);
  const showSectionToggle = isHomeRoute && sectionOptions.length > 1;
  const showVersionToggle = versionOptions.length > 1;
  const canEditDocUrl = useMemo(() => {
    if (isHomeRoute) {
      return true;
    }

    if (type === 'app') {
      return true;
    }

    if ((type === 'action' || type === 'authentication') && !!arg1) {
      return true;
    }

    return false;
  }, [isHomeRoute, type, arg1]);

  useEffect(() => {
    if (!isHomeRoute) {
      return;
    }

    if (availableAppSections.length === 0) {
      if (selectedAppLevelSection !== 'app') {
        setSelectedAppLevelSection('app');
      }
      return;
    }

    if (!availableAppSections.includes(selectedAppLevelSection)) {
      const fallback = availableAppSections.includes('app') ? 'app' : availableAppSections[0];
      setSelectedAppLevelSection(fallback);
    }
  }, [isHomeRoute, availableAppSections, selectedAppLevelSection]);

  const handleClose = () => {
    closeAppDocDrawer();
  };

  const handleDocUrlClick = () => {
    if (!canEditDocUrl) {
      return;
    }

    if (isHomeRoute) {
      setModalState('documentation', { open: true, mode: docUrl ? 'edit' : 'add', key: 'documentation.url', location: 'app' });
      return;
    }

    const key = type === 'app' ? 'documentation.url' : type === 'authentication' ? `authentication[id:${arg1}].documentation.url` : `actions[id:${arg1}].documentation.url`;
    const location = type === 'app' ? 'app' : type === 'authentication' ? 'auth' : 'action';
    setModalState('documentation', { open: true, mode: docUrl ? 'edit' : 'add', key, location });
  };

  useEffect(() => {
    if (availableVersions?.length > 0 && !availableVersions.includes(selectedVersion)) {
      setSelectedVersion(availableVersions[0]);
    }
  }, [availableVersions, selectedVersion]);

  if (!open) {
    return null;
  }

  return (
    <div className={`comp-argument-option-slider ${open ? 'open' : ''}`}>
      <div className={'backing'} onClick={handleClose} />
      <div className={'content docs-slider apps'}>
        <SliderHeader title={title} onClose={handleClose} />
        <div className="app-doc-drawer-body">
          <div className="app-doc-drawer-url-wrapper">
            <Input value={docUrl || ''} disabled placeholder="Documentation URL" />
            {canEditDocUrl && (
              <Button size="lg" onClick={handleDocUrlClick}>
                Edit
              </Button>
            )}
          </div>
          {(showSectionToggle || showVersionToggle) && (
            <div className="app-doc-drawer-toggle-row">
              <div className="app-doc-drawer-version-toggle">{showVersionToggle && <ToggleButton options={versionOptions} value={selectedVersion} onChange={setSelectedVersion} size="sm" fullWidth={false} />}</div>
              <div className="app-doc-drawer-section-toggle">{showSectionToggle && <ToggleButton options={sectionOptions} value={type} onChange={setSelectedAppLevelSection} size="sm" fullWidth={false} />}</div>
            </div>
          )}
          <AppDocContent appId={appId} type={type} objectId={objectId} docUrl={docUrl} version={selectedVersion} />
        </div>
      </div>
    </div>
  );
};

export default AppDocDrawer;
